import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Tooltip, message } from 'antd';
import { LeftOutlined, RightOutlined, CloseOutlined, SoundOutlined, InfoCircleOutlined, CopyOutlined, ZoomInOutlined, ZoomOutOutlined, SwapOutlined, ColumnHeightOutlined, UndoOutlined } from '@ant-design/icons';
import '@google/model-viewer';
import api from '../../../api/client';
import { useAuth } from '../../../context/AuthContext';
import PanoramaViewer from './PanoramaViewer';

const MediaPreviewModal = ({ visible, onClose, initialIndex, mediaItems = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [showInfo, setShowInfo] = useState(false);
    const { currentUser } = useAuth();
    const [token, setToken] = useState(null);

    // Zoom and flip state
    const [scale, setScale] = useState(1);
    const [flipH, setFlipH] = useState(false);
    const [flipV, setFlipV] = useState(false);

    // Pan state for dragging when zoomed
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });

    useEffect(() => {
        if (currentUser) {
            currentUser.getIdToken().then(setToken);
        }
    }, [currentUser]);

    // Update index when initialIndex changes or modal opens
    useEffect(() => {
        if (visible) {
            setCurrentIndex(initialIndex);
            setShowInfo(false); // Reset info panel on open
        }
    }, [visible, initialIndex]);

    // Reset transforms on navigation
    const resetTransforms = useCallback(() => {
        setScale(1);
        setFlipH(false);
        setFlipV(false);
        setPanX(0);
        setPanY(0);
    }, []);

    const handleNext = useCallback(() => {
        resetTransforms();
        setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
    }, [mediaItems.length, resetTransforms]);

    const handlePrev = useCallback(() => {
        resetTransforms();
        setCurrentIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
    }, [mediaItems.length, resetTransforms]);

    // Keyboard navigation
    useEffect(() => {
        if (!visible) return;

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'Escape') onClose();
            if (e.key.toLowerCase() === 'i') setShowInfo(prev => !prev);
            // Zoom controls
            if (e.key === '+' || e.key === '=') setScale(prev => Math.min(4, prev + 0.25));
            if (e.key === '-') setScale(prev => Math.max(0.25, prev - 0.25));
            if (e.key === '0') resetTransforms();
            // Flip controls
            if (e.key.toLowerCase() === 'h') setFlipH(prev => !prev);
            if (e.key.toLowerCase() === 'v') setFlipV(prev => !prev);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [visible, handleNext, handlePrev, onClose, resetTransforms]);

    if (!visible || !mediaItems[currentIndex]) return null;

    const item = mediaItems[currentIndex];

    const copyToClipboard = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        message.success('Prompt copied to clipboard');
    };

    const renderContent = () => {
        const getAuthUrl = (url) => {
            if (!url) return '';
            if (!token) return url;
            return `${api.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(url)}&token=${token}`;
        };

        switch (item.type) {
            case 'video':
                return (
                    <video
                        src={item.src}
                        controls
                        autoPlay
                        style={{ maxWidth: '100%', maxHeight: '80vh', outline: 'none' }}
                    />
                );
            case 'audio':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh', width: '100%' }}>
                        <SoundOutlined style={{ fontSize: 80, color: '#eb2f96', marginBottom: 32 }} />
                        <div style={{
                            width: '100%',
                            maxWidth: 700,
                            padding: '0 40px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            <audio
                                src={item.src}
                                controls
                                autoPlay
                                style={{
                                    width: '100%',
                                    height: 60,
                                    borderRadius: 12
                                }}
                            />
                            <style>{`
                                audio::-webkit-media-controls-panel {
                                    background: linear-gradient(to bottom, #2a2a2a, #1a1a1a);
                                    border-radius: 12px;
                                    padding: 10px 16px;
                                }
                                audio::-webkit-media-controls-current-time-display,
                                audio::-webkit-media-controls-time-remaining-display {
                                    font-size: 14px;
                                    color: #fff;
                                }
                                audio::-webkit-media-controls-timeline {
                                    height: 8px;
                                    border-radius: 4px;
                                    background: rgba(255,255,255,0.2);
                                }
                                audio::-webkit-media-controls-volume-slider {
                                    height: 8px;
                                }
                            `}</style>
                        </div>
                        {item.label && (
                            <div style={{
                                marginTop: 24,
                                color: '#8b949e',
                                fontSize: 14,
                                textAlign: 'center',
                                maxWidth: 600,
                                padding: '0 20px'
                            }}>
                                {item.label}
                            </div>
                        )}
                    </div>
                );
            case 'model':
                return (
                    <div style={{ width: '100%', height: '100%' }}>
                        <model-viewer
                            src={`${api.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(item.src)}&token=${token || ''}`}
                            camera-controls
                            auto-rotate
                            ar
                            shadow-intensity="1"
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );
            case 'image':
            default:
                if (item.metadata && (item.metadata.is_panorama || item.metadata.isPanorama)) {
                    return (
                        <div style={{ width: '100%', height: '100%' }}>
                            <PanoramaViewer imageUrl={getAuthUrl(item.src)} />
                        </div>
                    );
                }
                return (
                    <img
                        src={item.src}
                        alt={item.label}
                        draggable={false}
                        onMouseDown={(e) => {
                            if (scale > 1) {
                                e.preventDefault();
                                setIsDragging(true);
                                dragStart.current = { x: e.clientX - panX, y: e.clientY - panY };
                            }
                        }}
                        onMouseMove={(e) => {
                            if (isDragging && scale > 1) {
                                setPanX(e.clientX - dragStart.current.x);
                                setPanY(e.clientY - dragStart.current.y);
                            }
                        }}
                        onMouseUp={() => setIsDragging(false)}
                        onMouseLeave={() => setIsDragging(false)}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '80vh',
                            objectFit: 'contain',
                            transform: `translate(${panX}px, ${panY}px) scale(${scale}) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`,
                            transition: isDragging ? 'none' : 'transform 0.2s ease',
                            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                            userSelect: 'none'
                        }}
                    />
                );
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.9)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {/* Close Button */}
            <Button
                type="text"
                icon={<CloseOutlined style={{ fontSize: 24, color: '#fff' }} />}
                style={{ position: 'absolute', top: 20, right: 20, zIndex: 2000 }}
                onClick={onClose}
            />

            {/* Info Toggle Button */}
            {(item.prompt || item.model || item.provider || item.notes || item.folder || item.srefCode || item.fullPrompt) && (
                <Tooltip title="Show Info (I)">
                    <Button
                        type="text"
                        icon={<InfoCircleOutlined style={{ fontSize: 24, color: showInfo ? '#1890ff' : '#fff' }} />}
                        style={{ position: 'absolute', top: 20, right: 70, zIndex: 2000 }}
                        onClick={() => setShowInfo(!showInfo)}
                    />
                </Tooltip>
            )}

            {/* Content Area */}
            <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>

                {/* Prev Button */}
                <Button
                    type="text"
                    icon={<LeftOutlined style={{ fontSize: 24, color: '#fff' }} />}
                    style={{ position: 'absolute', left: 20, zIndex: 10 }}
                    onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                />

                {/* Media Content */}
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'margin-right 0.3s',
                    marginRight: showInfo ? 400 : 0
                }}>
                    {renderContent()}
                </div>

                {/* Info Panel */}
                <div style={{
                    position: 'absolute',
                    top: 60,
                    right: 80,
                    bottom: 60,
                    width: 300,
                    background: 'rgba(22, 27, 34, 0.95)',
                    backdropFilter: 'blur(10px)',
                    borderLeft: '1px solid #30363d',
                    padding: 20,
                    transform: showInfo ? 'translateX(0)' : 'translateX(400px)',
                    transition: 'transform 0.3s ease-in-out',
                    zIndex: 15,
                    overflowY: 'auto',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16
                }}>
                    {/* Header */}
                    <div style={{ borderBottom: '1px solid #30363d', paddingBottom: 12, marginBottom: 4 }}>
                        <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, wordBreak: 'break-word' }}>
                            {item.label}
                        </div>
                        <div style={{ color: '#8b949e', fontSize: 12, marginTop: 4 }}>
                            {item.type.toUpperCase()} • {new Date(item.createdAt || Date.now()).toLocaleDateString()}
                        </div>
                    </div>

                    {/* Model Info */}
                    {(item.model || item.provider) && (
                        <div>
                            <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>GENERATION MODEL</div>
                            <div style={{ color: '#c9d1d9', fontSize: 13, background: 'rgba(255,255,255,0.05)', padding: '6px 10px', borderRadius: 6 }}>
                                {item.provider && <span style={{ color: '#8b949e' }}>{item.provider} / </span>}
                                {item.model || 'Unknown Model'}
                            </div>
                        </div>
                    )}

                    {/* Sref Style Info */}
                    {item.srefCode && (
                        <div>
                            <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>MIDJOURNEY SREF</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <div style={{
                                    color: '#eb2f96',
                                    fontSize: 12,
                                    fontFamily: 'monospace',
                                    background: 'rgba(235, 47, 150, 0.15)',
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    border: '1px solid rgba(235, 47, 150, 0.3)'
                                }}>
                                    --sref {item.srefCode}
                                </div>
                                {item.styleVersion && (
                                    <div style={{
                                        color: '#eb2f96',
                                        fontSize: 12,
                                        fontFamily: 'monospace',
                                        background: 'rgba(235, 47, 150, 0.15)',
                                        padding: '6px 10px',
                                        borderRadius: 6,
                                        border: '1px solid rgba(235, 47, 150, 0.3)'
                                    }}>
                                        --sv {item.styleVersion}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Full Prompt (for sref styles) */}
                    {item.fullPrompt && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>FULL PROMPT</div>
                                <Tooltip title="Copy Prompt">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CopyOutlined style={{ fontSize: 12 }} />}
                                        onClick={() => copyToClipboard(item.fullPrompt)}
                                        style={{ color: '#8b949e' }}
                                    />
                                </Tooltip>
                            </div>
                            <div style={{
                                color: '#c9d1d9',
                                fontSize: 13,
                                lineHeight: 1.5,
                                background: 'rgba(0,0,0,0.2)',
                                padding: 12,
                                borderRadius: 6,
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                border: '1px solid #30363d',
                                flex: 1,
                                fontFamily: 'monospace'
                            }}>
                                {item.fullPrompt}
                            </div>
                        </div>
                    )}

                    {/* Folder (for inspiration items) */}
                    {item.folder && (
                        <div>
                            <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>FOLDER</div>
                            <div style={{ color: '#faad14', fontSize: 13, background: 'rgba(255,255,255,0.05)', padding: '6px 10px', borderRadius: 6, textTransform: 'capitalize' }}>
                                {item.folder}
                            </div>
                        </div>
                    )}

                    {/* Notes (for inspiration items) */}
                    {item.notes && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>NOTES</div>
                            <div style={{
                                color: '#c9d1d9',
                                fontSize: 13,
                                lineHeight: 1.5,
                                background: 'rgba(0,0,0,0.2)',
                                padding: 12,
                                borderRadius: 6,
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                border: '1px solid #30363d',
                                flex: 1
                            }}>
                                {item.notes}
                            </div>
                        </div>
                    )}

                    {/* Prompt */}
                    {item.prompt && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>PROMPT</div>
                                <Tooltip title="Copy Prompt">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CopyOutlined style={{ fontSize: 12 }} />}
                                        onClick={() => copyToClipboard(item.prompt)}
                                        style={{ color: '#8b949e' }}
                                    />
                                </Tooltip>
                            </div>
                            <div style={{
                                color: '#c9d1d9',
                                fontSize: 13,
                                lineHeight: 1.5,
                                background: 'rgba(0,0,0,0.2)',
                                padding: 12,
                                borderRadius: 6,
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                border: '1px solid #30363d',
                                flex: 1
                            }}>
                                {item.prompt}
                            </div>
                        </div>
                    )}

                    {/* Metadata */}
                    {item.metadata && Object.keys(item.metadata).length > 0 && (
                        <div>
                            <div style={{ color: '#8b949e', fontSize: 11, fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>DETAILS</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                {Object.entries(item.metadata).map(([key, value]) => (
                                    <div key={key} style={{ background: 'rgba(255,255,255,0.05)', padding: '6px 10px', borderRadius: 6 }}>
                                        <div style={{ color: '#8b949e', fontSize: 10, textTransform: 'uppercase' }}>{key}</div>
                                        <div style={{ color: '#fff', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>{typeof value === 'object' ? JSON.stringify(value) : value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Next Button */}
                <Button
                    type="text"
                    icon={<RightOutlined style={{ fontSize: 24, color: '#fff' }} />}
                    style={{ position: 'absolute', right: 20, zIndex: 10 }}
                    onClick={(e) => { e.stopPropagation(); handleNext(); }}
                />

                {/* Default Footer Info (Only show if Info Panel is closed) */}
                {!showInfo && (
                    <div style={{
                        position: 'absolute',
                        bottom: 20,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        color: '#fff',
                        pointerEvents: 'none',
                        transition: 'opacity 0.2s',
                        opacity: showInfo ? 0 : 1
                    }}>
                        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{item.label}</div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>
                            {currentIndex + 1} / {mediaItems.length}
                        </div>
                        {(item.prompt || item.model || item.notes || item.srefCode) && <div style={{ fontSize: 11, color: '#8b949e', marginTop: 4 }}>Press 'I' for details</div>}
                    </div>
                )}

                {/* Zoom/Flip Controls - Only for images */}
                {(item.type === 'image' || (!item.type && !item.metadata?.is_panorama && !item.metadata?.isPanorama)) && (
                    <div style={{
                        position: 'absolute',
                        bottom: 80,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 8,
                        background: 'rgba(22, 27, 34, 0.9)',
                        backdropFilter: 'blur(10px)',
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: '1px solid #30363d',
                        zIndex: 20
                    }}>
                        <Tooltip title="Zoom Out (-)">
                            <Button
                                type="text"
                                size="small"
                                icon={<ZoomOutOutlined style={{ color: '#fff' }} />}
                                onClick={() => setScale(prev => Math.max(0.25, prev - 0.25))}
                                disabled={scale <= 0.25}
                            />
                        </Tooltip>
                        <Tooltip title="Reset (0)">
                            <Button
                                type="text"
                                size="small"
                                icon={<UndoOutlined style={{ color: '#fff' }} />}
                                onClick={resetTransforms}
                            />
                        </Tooltip>
                        <Tooltip title="Zoom In (+)">
                            <Button
                                type="text"
                                size="small"
                                icon={<ZoomInOutlined style={{ color: '#fff' }} />}
                                onClick={() => setScale(prev => Math.min(4, prev + 0.25))}
                                disabled={scale >= 4}
                            />
                        </Tooltip>
                        <div style={{ width: 1, background: '#30363d', margin: '0 4px' }} />
                        <Tooltip title="Flip Horizontal (H)">
                            <Button
                                type="text"
                                size="small"
                                icon={<SwapOutlined style={{ color: flipH ? '#1890ff' : '#fff' }} />}
                                onClick={() => setFlipH(prev => !prev)}
                            />
                        </Tooltip>
                        <Tooltip title="Flip Vertical (V)">
                            <Button
                                type="text"
                                size="small"
                                icon={<ColumnHeightOutlined style={{ color: flipV ? '#1890ff' : '#fff' }} />}
                                onClick={() => setFlipV(prev => !prev)}
                            />
                        </Tooltip>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MediaPreviewModal;
